import { singularize } from 'ember-inflector';

/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      lib util — Ember Data model / custom-field state helpers (default CF setup,
 *              smart CF sync on state change, modelVar display config for index/detail views)
 *   extends:   -
 *   template:  none
 *   args:      -
 *   uses:      ember-inflector (singularize)
 *   siblings:  sibling utility/custom-fields.js, utility/strings.js, utility/ajax.js
 *              (re-exported together via the ../utility.js barrel)
 *   tests:     tests/unit/lib/models-wave6-test.js, tests/unit/lib/utility-extra-test.js,
 *              tests/unit/lib/utility-wave4-test.js, tests/unit/lib/utility-wave5-test.js
 *   gotchas:   smartUpdateCustomFieldsForState mutates the passed `customFields` array in
 *              place (splice/push); for a NEW record it also unloads the removed custom_field
 *              records from the store, while for an EXISTING record removal is visual-only —
 *              the server stays source of truth for that record's custom_fields.
 *
 * @module beebee-base/lib/utility/models
 * @description Model & model-variable utilities.
 */


/**
 * Imposta i custom fields di default per un modello
 * @param {Object} me - Utente corrente
 * @param {Object} model - Modello Ember Data
 * @param {Object} store - Ember Data store
 */
async function setDefaultCustomField(me, model, store) {
  const modelName = model.constructor.modelName;
  const organization = me.organization;

  // Initialize custom fields
  const tmpCFS = model.id ? await model.get('custom_fields') : [];

  // Set default state
  let defaultState = model.get(`${modelName}_state`);
  if (!defaultState) {
    const states = organization?.get(`${modelName}_states`);
    if (states?.length) {
      defaultState = states.find(item => item.status_default) || states[0];
      if (defaultState) {
        model.set(`${modelName}_state`, defaultState);
      }
    }
  }

  // Filter and sort organization custom fields
  const organizationCustomFields = organization?.organization_custom_fields || [];
  const sortedCustomFields = organizationCustomFields.filter(item => item.get(`in_${modelName}`) === true).sort((a, b) => (a.organization_custom_field?.rank ?? Infinity) - (b.organization_custom_field?.rank ?? Infinity));

  // Add matching custom fields to model
  sortedCustomFields.forEach(organizationCustomField => {
    if (organizationCustomField.get('disabled')) return;
    const stateId = organizationCustomField.get(`${modelName}_state.id`);
    const stateMatches = !stateId || defaultState?.id === stateId;
    if (stateMatches) {
      const newCustomField = store.createRecord('custom_field', {
        organization_custom_field: organizationCustomField
      });
      // tmpCFS is resolved custom_fields hasMany OR native []; native push works for both
      tmpCFS.push(newCustomField);
    }
  });
  model.set('custom_fields', tmpCFS);
}

/**
 * Verifica se un custom_field ha dati compilati, in base al data_type dell'OCF.
 * Segue la stessa logica di edit-custom-field.js:
 *   number / currency / range → number_value
 *   checkbox                  → boolean_value
 *   calculated                → sempre false (server-computed)
 *   tutti gli altri           → value
 * @param {Object} cf - custom_field record
 * @returns {boolean}
 */
function cfHasData(cf) {
  const dataType = cf.get('organization_custom_field.data_type');
  if (dataType === 'calculated') return false;
  if (dataType === 'number' || dataType === 'currency' || dataType === 'range') {
    const v = cf.number_value;
    return v !== null && v !== undefined;
  }
  if (dataType === 'checkbox') {
    return cf.boolean_value === true;
  }
  const val = cf.value;
  return val !== null && val !== undefined && val !== '';
}

/**
 * Restituisce i custom_fields del modello che verrebbero rimossi cambiando allo stato newStateId.
 * Sono quelli il cui OCF è filtrato per uno stato specifico diverso da newStateId.
 * I campi state-agnostic (nessun filtro di stato) vengono sempre mantenuti.
 * @param {Object} model - Ember Data model
 * @param {string} newStateId - id del nuovo stato
 * @param {Array} customFields - custom_fields già risolti (yield model.custom_fields)
 * @returns {Array}
 */
function getFieldsToRemove(model, newStateId, customFields) {
  const fields = customFields;
  if (!fields || !fields.length) return [];
  const modelName = model.constructor.modelName;
  return fields.filter(cf => {
    const stateId = cf.get(`organization_custom_field.${modelName}_state.id`);
    return stateId && stateId !== newStateId;
  });
}

/**
 * Verifica se cambiare al nuovo stato causerà perdita di dati compilati dall'utente.
 * @param {Object} model - Ember Data model
 * @param {string} newStateId - id del nuovo stato
 * @param {Array} customFields - custom_fields già risolti (yield model.custom_fields)
 * @returns {boolean}
 */
function stateChangeWillLoseData(model, newStateId, customFields) {
  return getFieldsToRemove(model, newStateId, customFields).some(cf => cfHasData(cf));
}

/**
 * Aggiorna i custom_fields di un item cambiando stato, in modo smart.
 *
 * Nuovo record (model.isNew):
 * - Rimuove i campi state-specific che non appartengono al nuovo stato (unload dal store)
 * - Mantiene i campi state-agnostic e quelli già corretti per il nuovo stato
 * - Aggiunge i campi mancanti per il nuovo stato (senza dati)
 *
 * Record esistente (!model.isNew):
 * - Rimuove solo visivamente i campi dell'old-state per feedback immediato
 * - Non crea record nel store (il server ritorna i CF corretti dopo il salvataggio)
 *
 * @param {Object} me - Utente corrente (session.me)
 * @param {Object} model - Ember Data model
 * @param {Object} newState - record [model]_state (deve essere non-null)
 * @param {Object} store - Ember Data store
 * @param {Array} customFields - custom_fields già risolti (yield model.custom_fields)
 */
function smartUpdateCustomFieldsForState(me, model, newState, store, customFields) {
  if (!customFields || !newState) return;
  const modelName = model.constructor.modelName;
  const toRemove = getFieldsToRemove(model, newState.id, customFields);
  if (!model.isNew) {
    // Per record esistenti: solo rimozione visiva, il server è source of truth sui CF
    toRemove.forEach(cf => {
      const _i = customFields.indexOf(cf);
      if (_i !== -1) customFields.splice(_i, 1);
    });
    return;
  }

  // Per nuovi record: rimozione + unload + aggiunta CF del nuovo stato
  toRemove.forEach(cf => {
    const _i = customFields.indexOf(cf);
    if (_i !== -1) customFields.splice(_i, 1);
    if (cf.isNew) store.unloadRecord(cf);
  });
  const organization = me.organization;
  const orgCustomFields = organization?.organization_custom_fields || [];
  const relevantOcfs = orgCustomFields.filter(ocf => ocf.get(`in_${modelName}`) === true);
  const existingOcfIds = new Set(customFields.map(cf => cf.get('organization_custom_field.id')).filter(Boolean));
  relevantOcfs.forEach(ocf => {
    if (ocf.get('disabled')) return;
    const stateId = ocf.get(`${modelName}_state.id`);
    const belongsToNewState = !stateId || stateId === newState.id;
    if (belongsToNewState && !existingOcfIds.has(ocf.id)) {
      const newCf = store.createRecord('custom_field', {
        organization_custom_field: ocf
      });
      customFields.push(newCf);
    }
  });
}

/** Configurazione icone e route per modelli */
const MODEL_CONFIG = Object.freeze({
  contact: {
    icon: 'user',
    stateKey: 'contact_state'
  },
  product: {
    icon: 'shopping-basket',
    stateKey: 'product_state'
  },
  project: {
    icon: 'file-chart-line',
    stateKey: 'project_state'
  },
  event: {
    icon: 'calendar-day',
    stateKey: 'event_state'
  },
  workhour: {
    icon: 'stopwatch',
    stateKey: 'workhour_state'
  },
  calendar: {
    icon: 'calendar-day',
    translateStatus: true
  }
});

/** Modelli documento (usano numero come status) */
const DOCUMENT_MODELS = Object.freeze(['expense', 'estimate', 'order', 'invoice', 'autofattura', 'receipt', 'contract', 'ddt', 'payment']);

/**
 * Ottiene variabili di visualizzazione per un modello
 * @param {Object|string} modelObj - Modello o nome modello
 * @param {boolean} isIndex - Se è vista index
 * @returns {Object} Oggetto con icon, status, translateStatus, index
 */
function modelVar(modelObj, isIndex) {
  const toSwitch = isIndex ? singularize(modelObj) : modelObj?.constructor?.modelName;
  const defaultIndex = isIndex ? '' : `${toSwitch}s`;

  // Modelli documento
  if (DOCUMENT_MODELS.includes(toSwitch)) {
    return {
      icon: 'file-invoice-dollar',
      status: isIndex ? 'list' : modelObj?.number,
      translateStatus: isIndex,
      index: `${singularize(toSwitch)}s`
    };
  }

  // Calendario (caso speciale)
  if (toSwitch === 'calendar') {
    return {
      icon: 'calendar-day',
      status: 'calendar',
      translateStatus: true,
      index: 'calendar'
    };
  }

  // Modelli con configurazione
  const config = MODEL_CONFIG[toSwitch];
  if (config) {
    return {
      icon: config.icon,
      status: isIndex ? 'list' : modelObj?.get(`${config.stateKey}.status_name`),
      translateStatus: isIndex || config.translateStatus || false,
      index: `${toSwitch}s`
    };
  }

  // Default
  return {
    icon: '',
    status: isIndex ? 'list' : '',
    translateStatus: isIndex,
    index: defaultIndex
  };
}

export { cfHasData, getFieldsToRemove, modelVar, setDefaultCustomField, smartUpdateCustomFieldsForState, stateChangeWillLoseData };
//# sourceMappingURL=models.js.map
